import React, { useState, useEffect } from 'react';
import { formatResponse, parseTodoPlan, parseContentWithWidgets } from '../utils/formatResponse';
import TodoPlanCard from './TodoPlanCard';
import Todo from './Todo';
import ArticleCard from './ArticleCard';
import NewsCard from './NewsCard';
import TestimonialCard from './TestimonialCard';
import PartnerCard from './PartnerCard';
import VideoCard from './VideoCard';
import CoachCard from './CoachCard';
import { todosAPI } from '../services/api';
import stateManager from '../services/stateManager';
import styles from '../styles/MessageBubble.module.css';

function MessageBubble({ role, content, timestamp, conversationId, currentMission, onPlanApprove, onPlanEdit, onArticleClick, onNewsClick, onCoachClick }) {
  // Track todos state for re-rendering when singleton updates
  const [cachedTodos, setCachedTodos] = useState(stateManager.getTodos() || []);

  // Subscribe to todos changes from singleton
  useEffect(() => {
    const unsubscribe = stateManager.subscribe('todos', (updatedTodos) => {
      // console.log('[MessageBubble] Received todos update from singleton');
      setCachedTodos(updatedTodos);
    });

    return unsubscribe;
  }, []);

  // Handle todo toggle in chat view
  const handleTodoToggle = async ({ todoId, onComplete, onError }) => {
    try {
      // console.log('[MessageBubble] Toggling todo:', todoId);

      // Call API
      await todosAPI.toggleTodo(todoId);
      // console.log('[MessageBubble] API call successful');

      // Refresh todos from server to get actual state
      const freshTodos = await todosAPI.getTodos(conversationId);
      // console.log('[MessageBubble] Refreshed todos from server');

      // Update singleton cache
      stateManager.setTodos(freshTodos);

      // Notify success
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('[MessageBubble] Failed to toggle todo:', error);

      // Notify error
      if (onError) {
        onError(error.message || 'Failed to update todo');
      }
    }
  };
  if (role === 'user') {
    return (
      <div className={styles.userMessage}>
        <div className={styles.userBubble}>
          {content}
        </div>
      </div>
    );
  }

  if (role === 'assistant') {
    // Check if this message contains a TodoPlan
    const todoPlan = parseTodoPlan(content);

    if (todoPlan) {
      // todoPlan is now { objective: "...", tiers: [...] }
      // Extract text before and after the TodoPlan tag
      const todoPlanRegex = /<TodoPlan:\{[\s\S]*?\}>/;
      const parts = content.split(todoPlanRegex);
      const textBefore = parts[0] ? formatResponse(parts[0].trim()) : '';
      const textAfter = parts[1] ? formatResponse(parts[1].trim()) : '';

      // Render text + TodoPlanCard + text
      return (
        <div className={styles.assistantMessage}>
          <div className={styles.assistantContent}>
            {textBefore && (
              <div
                className={styles.assistantBubble}
                dangerouslySetInnerHTML={{ __html: textBefore }}
              />
            )}
            <TodoPlanCard
              plan={todoPlan.tiers}
              objective={todoPlan.objective}
              oldObjective={currentMission}
              conversationId={conversationId}
              onApprove={onPlanApprove}
              onEdit={onPlanEdit}
              onCancel={onPlanEdit ? () => onPlanEdit("I'll keep my current plan, thanks.") : undefined}
            />
            {textAfter && (
              <div
                className={styles.assistantBubble}
                dangerouslySetInnerHTML={{ __html: textAfter }}
              />
            )}
          </div>
        </div>
      );
    }

    // Check if message contains interactive widgets/cards
    const contentParts = parseContentWithWidgets(content);

    if (contentParts.hasWidgets) {
      // Use live todos from state (subscribed to singleton changes)
      const todosById = {};
      cachedTodos.forEach(stage => {
        stage.todos.forEach(todo => {
          todosById[todo.id] = todo;
        });
      });

      // Render mixed content with interactive components
      return (
        <div className={styles.assistantMessage}>
          <div className={styles.assistantContent}>
            {contentParts.parts.map((part, index) => {
              if (part.type === 'text') {
                return (
                  <span
                    key={index}
                    dangerouslySetInnerHTML={{ __html: formatResponse(part.content) }}
                  />
                );
              } else if (part.type === 'widget') {
                // IMPORTANT: Use widget tag data (most recent), NOT cached singleton data
                // Widget tags reflect the exact state at the time of the message
                // Singleton cache may be stale if todos haven't been refetched yet
                const isCompleted = part.data.isCompleted;
                const isOptional = part.data.isOptional;
                const todoText = part.data.todoText;

                return (
                  <Todo
                    key={part.data.todoId} // Use todoId as key for proper reactivity
                    todoId={part.data.todoId}
                    todoText={todoText}
                    isCompleted={isCompleted}
                    isOptional={isOptional}
                    variant="chat"
                    onToggle={handleTodoToggle}
                  />
                );
              } else if (part.type === 'article') {
                return (
                  <ArticleCard
                    key={index}
                    title={part.data.title}
                    slug={part.data.slug}
                    category={part.data.category}
                    excerpt={part.data.excerpt}
                    heroImage={part.data.heroImage}
                    onClick={onArticleClick}
                  />
                );
              } else if (part.type === 'news') {
                return (
                  <NewsCard
                    key={index}
                    id={part.data.id}
                    headline={part.data.headline}
                    sourceUrl={part.data.sourceUrl}
                    publishedDate={part.data.publishedDate}
                    summary={part.data.summary}
                    heroImage={part.data.heroImage}
                    onClick={onNewsClick}
                  />
                );
              } else if (part.type === 'testimonial') {
                return (
                  <TestimonialCard
                    key={index}
                    userName={part.data.userName}
                    achievement={part.data.achievement}
                    testimonialText={part.data.testimonialText}
                    imageUrl={part.data.imageUrl}
                    date={part.data.date}
                  />
                );
              } else if (part.type === 'partner') {
                return (
                  <PartnerCard
                    key={index}
                    name={part.data.name}
                    websiteUrl={part.data.websiteUrl}
                    bestFor={part.data.bestFor}
                    imageUrl={part.data.imageUrl}
                    description={part.data.description}
                  />
                );
              } else if (part.type === 'video') {
                return (
                  <VideoCard
                    key={index}
                    id={part.data.id}
                    videoId={part.data.videoId}
                    title={part.data.title}
                    courseName={part.data.courseName}
                    description={part.data.description}
                    videoUrl={part.data.videoUrl}
                    lessonUrl={part.data.lessonUrl}
                    thumbnailUrl={part.data.thumbnailUrl}
                  />
                );
              } else if (part.type === 'coach') {
                return (
                  <CoachCard
                    key={index}
                    id={part.data.id}
                    displayName={part.data.displayName}
                    bio={part.data.bio}
                    specializations={part.data.specializations}
                    sessionDuration={part.data.sessionDuration}
                    profileImage={part.data.profileImage}
                    similarity={part.data.similarity}
                    onSelect={onCoachClick}
                  />
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }

    // For regular messages, use formatted HTML
    const formattedContent = formatResponse(content, conversationId, currentMission, onPlanEdit);
    return (
      <div className={styles.assistantMessage}>
        <div
          className={styles.assistantContent}
          dangerouslySetInnerHTML={{ __html: formattedContent }}
        />
      </div>
    );
  }

  return null;
}

export default MessageBubble;
